/**
 * SteppedConsultationFormV2 — Project Evaluation Form (redesign).
 *
 * Reviewable behind the `intakeV2Enabled()` feature flag. V1 remains the
 * default production flow. See src/lib/intakeV2Flag.ts.
 *
 * Behavior differences vs V1:
 *  - Step 1 uses outcome-based cards including an "I am not sure yet" path.
 *  - Step 2 is required-marked, adds "Not sure yet" everywhere.
 *  - Step 3 replaces the nested DrawingsIntake with a single multi-select
 *    "documents_available" question, keeps the VA soft-switch card, and
 *    swaps the legal checkbox for a required "who prepared the design"
 *    single-select question.
 *  - Step 4 adds expanded role list and conditionally-required company.
 *  - Success screen uses the premium "What happens next" module.
 *
 * Backend routing:
 *  - design    → source_form: "lighting_design"
 *  - value_a.  → source_form: "value_analysis"
 *  - unsure    → source_form: "consultation"  (existing generic label)
 * Additive detail fields do NOT change V1 payloads.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import BtnArrow from "@/components/ui/btn-arrow";
import { BtnSpinner } from "@/components/ui/btn-spinner";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import FileUploader, {
  UploadedFile,
  activeUploadProgress,
  uploadRefs,
} from "@/components/forms/FileUploader";
import { snapshotJourney, record, type EventKind } from "@/lib/journey";
import { useUnloadWarning } from "@/hooks/useUnloadWarning";
import { FORM_PROJECT_TYPES } from "@/data/projectTypes";

/* ---------------------------- Constants ---------------------------- */

export type V2Path = "design" | "value_analysis" | "unsure";

export const V2_ROLES = [
  "Developer / Owner",
  "Architect",
  "Interior Designer",
  "General Contractor",
  "Electrical Contractor",
  "Engineer / Consultant",
  "Owner's Representative / Project Manager",
  "Lighting Designer / Consultant",
  "Manufacturer / Integrator",
  "Homeowner / Private Client",
  "Other",
] as const;

const PRIVATE_ROLE = "Homeowner / Private Client";

export const V2_DESIGN_STAGES = [
  "Early planning / pre-design",
  "Design in progress",
  "Drawings issued, pre-construction",
  "Out for bid, pre-construction",
  "Under construction",
  "Not sure yet",
] as const;

export const V2_VA_STAGES = [
  "Design development",
  "Construction documents",
  "Issued for bid",
  "Pre-construction",
  "Procurement",
  "Under construction",
  "Not sure yet",
] as const;

export const V2_UNSURE_STAGES = [
  "Just exploring",
  "Concept stage",
  "Design in progress",
  "Drawings ready",
  "Under construction",
  "Not sure yet",
] as const;

const DESIGN_PRIORITIES = [
  "Atmosphere and architecture",
  "Visual comfort",
  "Controls and scenes",
  "Energy and maintenance",
  "Budget alignment",
  "Schedule certainty",
] as const;

const VA_PRESSURES = [
  "Cost",
  "Lead time",
  "Availability",
  "Performance",
  "Controls compatibility",
  "Constructability",
] as const;

const DESIGN_DOCS = [
  "Floor plans",
  "Reflected ceiling plans",
  "Electrical or lighting plans",
  "Fixture schedule",
  "Specifications",
  "Renderings or reference images",
  "Nothing yet",
  "Not sure",
] as const;

const VA_DOCS = [
  "Floor plans",
  "Reflected ceiling plans",
  "Electrical or lighting plans",
  "Lighting layout",
  "Fixture schedule",
  "Specifications",
  "RFIs or addenda",
  "Not sure",
] as const;

/** Options that clear all others when picked, and are cleared by any real pick. */
const DOC_EXCLUSIVE = new Set(["Nothing yet", "Not sure"]);

/**
 * Pure helper for document/package multi-select mutual-exclusivity.
 * Extracted so unit tests can pin the rules without mounting the form.
 *
 * Rules:
 *  - Design / Unsure: "Nothing yet" and "Not sure" are exclusive.
 *  - Project Review: only "Not sure" is exclusive.
 *  - Picking an exclusive option clears every other selection.
 *  - Picking a real option clears any exclusive options currently set.
 *  - Toggling the same option twice removes it.
 */
export function applyDocToggle(
  current: string[],
  value: string,
  variant: "design" | "unsure" | "value_analysis",
): string[] {
  const isVA = variant === "value_analysis";
  const isExclusive = isVA ? value === "Not sure" : DOC_EXCLUSIVE.has(value);
  if (current.includes(value)) return current.filter((v) => v !== value);
  if (isExclusive) return [value];
  const cleared = current.filter((v) =>
    isVA ? v !== "Not sure" : !DOC_EXCLUSIVE.has(v),
  );
  return [...cleared, value];
}

const VA_PREPARED_BY = [
  "Another architect, engineer, designer, or consultant",
  "My team or company",
  "Not sure",
] as const;

/* ---------------------------- Styling ---------------------------- */

const inputCls =
  "w-full min-h-[44px] bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 transition-colors font-light";
const triggerCls =
  "w-full min-h-[44px] bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-3 text-sm text-white/70 data-[placeholder]:text-white/40 focus:ring-0 focus:ring-offset-0 focus:border-white/60 transition-colors h-auto font-light";
const itemCls =
  "font-light text-white/85 focus:bg-white/10 focus:text-white data-[highlighted]:bg-white/10 data-[highlighted]:text-white data-[state=checked]:text-white";
const chipBase =
  "px-4 py-2 min-h-[36px] text-xs font-light tracking-[0.05em] border transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal";
const chipInactive = "border-white/15 text-white/70 hover:border-white/40 hover:text-white";
const chipActive = "border-white bg-white/[0.06] text-white";
const eyebrowCls = "text-white/45 font-sans font-light text-[10px] tracking-[0.25em] uppercase";
const requiredMark = <span className="text-white/40 ml-1" aria-hidden>*</span>;
const optionalMark = <span className="text-white/35 ml-1">(optional)</span>;

/* ---------------------------- Schemas ---------------------------- */

export const v2ProjectSchema = z.object({
  projectType: z.string().min(1, "Pick the closest match. We can refine it later."),
  projectLocation: z
    .string()
    .trim()
    .min(1, "Add the city, state, or country where the project is located.")
    .max(200),
  projectStage: z.string().min(1, "Choose the option closest to where the project is today."),
  projectName: z.string().trim().max(200).optional().or(z.literal("")),
});

export function v2AboutSchema(role: string) {
  const companyRule =
    role && role !== PRIVATE_ROLE
      ? z.string().trim().min(1, "Add your company or studio.").max(150)
      : z.string().trim().max(150).optional().or(z.literal(""));
  return z.object({
    name: z.string().trim().min(1, "How should we address you?").max(100),
    email: z
      .string()
      .trim()
      .email("Double-check the email. We will send your confirmation there.")
      .max(255),
    phone: z.string().trim().max(50).optional().or(z.literal("")),
    company: companyRule,
    role: z.enum(V2_ROLES, {
      errorMap: () => ({
        message: "Choose the role closest to your involvement in the project.",
      }),
    }),
  });
}

export const v2UnsureNoteSchema = z
  .string()
  .trim()
  .min(1, "Tell us briefly what is decided, what is missing, or what needs to happen next.")
  .max(800);

/** Map a chosen path to the backend `source_form` value. */
export function v2PathToSourceForm(path: V2Path): string {
  if (path === "design") return "lighting_design";
  if (path === "value_analysis") return "value_analysis";
  return "consultation"; // unsure → existing "Request a Project Evaluation"
}

/* ---------------------------- Props ---------------------------- */

interface Props {
  defaultPath?: V2Path;
  onLabsClick?: () => void;
  resetKey?: number;
}

type FieldKey =
  | "projectType"
  | "projectLocation"
  | "projectStage"
  | "name"
  | "email"
  | "company"
  | "role"
  | "unsureNotes"
  | "vaPreparedBy";

/* ---------------------------- Component ---------------------------- */

const SteppedConsultationFormV2 = ({ defaultPath, onLabsClick, resetKey }: Props) => {
  const [path, setPath] = useState<V2Path | null>(defaultPath ?? null);
  const [step, setStep] = useState<number>(1);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [errorSummary, setErrorSummary] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<null | V2Path>(null);
  const [successRef, setSuccessRef] = useState<string | null>(null);
  const [copiedRef, setCopiedRef] = useState(false);
  const [pathSwitchedFromVA, setPathSwitchedFromVA] = useState(false);
  const [vaOverrideKeepPath, setVaOverrideKeepPath] = useState(false);
  const vaSwitchShownRef = useRef(false);

  const [intake, setIntake] = useState({
    projectType: "",
    projectLocation: "",
    projectStage: "",
    projectName: "",
    documentsAvailable: [] as string[],
    designPriorities: [] as string[],
    designNote: "",
    vaPreparedBy: "",
    vaPressures: [] as string[],
    vaPrompt: "",
    unsureNotes: "",
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
  });

  const [files, setFiles] = useState<UploadedFile[]>([]);
  const uploadPct = activeUploadProgress(files);
  const uploadsPending = files.some((f) => f.status === "uploading" || f.status === "queued");
  const uploadsFailed = files.some((f) => f.status === "failed");
  const [hp, setHp] = useState("");
  const startedAtRef = useRef<number>(Date.now());
  const stepTopRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useUnloadWarning(uploadsPending);

  /* -------- Reset on parent resetKey change -------- */
  useEffect(() => {
    setPath(defaultPath ?? null);
    setStep(1);
    setErrors({});
    setErrorSummary([]);
    setSuccess(null);
    setSuccessRef(null);
    setCopiedRef(false);
    setPathSwitchedFromVA(false);
    setVaOverrideKeepPath(false);
    vaSwitchShownRef.current = false;
    setFiles([]);
    setIntake({
      projectType: "",
      projectLocation: "",
      projectStage: "",
      projectName: "",
      documentsAvailable: [],
      designPriorities: [],
      designNote: "",
      vaPreparedBy: "",
      vaPressures: [],
      vaPrompt: "",
      unsureNotes: "",
      name: "",
      email: "",
      phone: "",
      company: "",
      role: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    stepTopRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    if (path) safeRecord("intake_v2_view", `${path}:step${step}`);
    else safeRecord("intake_v2_view", `unset:step${step}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const totalSteps = 4;
  const stageOptions = useMemo(() => {
    if (path === "value_analysis") return V2_VA_STAGES;
    if (path === "unsure") return V2_UNSURE_STAGES;
    return V2_DESIGN_STAGES;
  }, [path]);
  const stageLabel =
    path === "value_analysis"
      ? "Package stage"
      : path === "unsure"
        ? "Where does the project stand?"
        : "Project stage";
  const stepNames = ["Starting Point", "About the Project", "Details", "About You"];

  /* -------- Helpers -------- */
  const setField = (key: FieldKey, value: string) => {
    setIntake((p) => ({ ...p, [key]: value } as typeof p));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const togglePriority = (key: "designPriorities" | "vaPressures", value: string, max = 3) =>
    setIntake((prev) => {
      const arr = prev[key];
      if (arr.includes(value)) return { ...prev, [key]: arr.filter((v) => v !== value) };
      if (arr.length >= max) return prev;
      return { ...prev, [key]: [...arr, value] };
    });

  const toggleDoc = (value: string) =>
    setIntake((prev) => ({
      ...prev,
      documentsAvailable: applyDocToggle(
        prev.documentsAvailable,
        value,
        path === "value_analysis" ? "value_analysis" : path === "unsure" ? "unsure" : "design",
      ),
    }));

  const vaRecommendsSwitch =
    path === "value_analysis" &&
    intake.documentsAvailable.length > 0 &&
    !intake.documentsAvailable.includes("Fixture schedule") &&
    !intake.documentsAvailable.includes("Lighting layout") &&
    !intake.documentsAvailable.includes("Not sure");

  // Emit va_switch_shown exactly once per display cycle. If the card
  // disappears (user changes answers) and later re-appears, that is a
  // new cycle and warrants a new shown event.
  const shouldShowSwitchCard = vaRecommendsSwitch && !vaOverrideKeepPath;
  useEffect(() => {
    if (shouldShowSwitchCard && !vaSwitchShownRef.current) {
      vaSwitchShownRef.current = true;
      safeRecord("intake_v2_va_switch_shown", path ?? "");
    } else if (!shouldShowSwitchCard && vaSwitchShownRef.current) {
      vaSwitchShownRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowSwitchCard]);


  /* -------- Nav -------- */
  const showErrors = (nextErrors: Partial<Record<FieldKey, string>>, summary: string[]) => {
    setErrors(nextErrors);
    setErrorSummary(summary);
    requestAnimationFrame(() => {
      errorRef.current?.focus();
      errorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    Object.keys(nextErrors).forEach((k) =>
      safeRecord("intake_v2_error", `${path ?? "unset"}:step${step}:${k}`),
    );
  };

  const goNext = () => {
    if (uploadsPending) {
      toast.error("Uploads in progress", { description: "Please wait for files to finish." });
      return;
    }
    if (uploadsFailed) {
      toast.error("Some uploads failed", {
        description: "Remove failed files or retry before continuing.",
      });
      return;
    }
    if (step === 1) {
      if (!path) {
        showErrors({}, ["Choose what you would like help with."]);
        return;
      }
      setErrorSummary([]);
      safeRecord("intake_v2_continue", `${path}:1`);
      setStep(2);
      return;
    }
    if (step === 2) {
      const parsed = v2ProjectSchema.safeParse({
        projectType: intake.projectType,
        projectLocation: intake.projectLocation,
        projectStage: intake.projectStage,
        projectName: intake.projectName,
      });
      if (!parsed.success) {
        const next: Partial<Record<FieldKey, string>> = {};
        const summary: string[] = [];
        for (const issue of parsed.error.issues) {
          const k = issue.path[0] as FieldKey | undefined;
          if (k && !next[k]) {
            next[k] = issue.message;
            summary.push(issue.message);
          }
        }
        showErrors(next, summary);
        return;
      }
      setErrorSummary([]);
      safeRecord("intake_v2_continue", `${path}:2`);
      setStep(3);
      return;
    }
    if (step === 3) {
      const next: Partial<Record<FieldKey, string>> = {};
      const summary: string[] = [];
      if (path === "unsure") {
        const parsed = v2UnsureNoteSchema.safeParse(intake.unsureNotes);
        if (!parsed.success) {
          next.unsureNotes = parsed.error.issues[0]?.message ?? "Required";
          summary.push(next.unsureNotes);
        }
      }
      if (path === "value_analysis" && !intake.vaPreparedBy) {
        next.vaPreparedBy = "Tell us who prepared the current lighting design.";
        summary.push(next.vaPreparedBy);
      }
      if (summary.length) {
        showErrors(next, summary);
        return;
      }
      setErrorSummary([]);
      safeRecord("intake_v2_continue", `${path}:3`);
      setStep(4);
      return;
    }
  };

  const goBack = () => {
    safeRecord("intake_v2_back", `${path}:${step}`);
    setErrorSummary([]);
    setStep((s) => Math.max(1, s - 1));
  };

  /* -------- Submit -------- */
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!path) return;
    const parsed = v2AboutSchema(intake.role).safeParse({
      name: intake.name,
      email: intake.email,
      phone: intake.phone,
      company: intake.company,
      role: intake.role,
    });
    if (!parsed.success) {
      const next: Partial<Record<FieldKey, string>> = {};
      const summary: string[] = [];
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as FieldKey | undefined;
        if (k && !next[k]) {
          next[k] = issue.message;
          summary.push(issue.message);
        }
      }
      showErrors(next, summary);
      return;
    }
    if (uploadsPending || uploadsFailed) {
      toast.error("Uploads not ready", {
        description: "Wait for uploads to finish, retry, or remove failed files.",
      });
      return;
    }

    setSubmitting(true);
    const t0 = startedAtRef.current;
    try {
      const uploads = uploadRefs(files);
      const journey = snapshotJourney();
      const source_form = v2PathToSourceForm(path);

      const notesParts: string[] = [];
      if (path === "unsure") notesParts.push(`User notes: ${intake.unsureNotes}`);
      if (path === "design" && intake.designNote)
        notesParts.push(`Additional context: ${intake.designNote}`);
      if (path === "value_analysis" && intake.vaPrompt)
        notesParts.push(`What prompted the review: ${intake.vaPrompt}`);

      const crmRes = await supabase.functions.invoke("send-form-submission", {
        body: {
          full_name: parsed.data.name,
          email: parsed.data.email,
          company: parsed.data.company || undefined,
          phone: parsed.data.phone || undefined,
          message: notesParts.join("\n\n"),
          source_form,
          details: {
            path,
            role: parsed.data.role,
            project_name: intake.projectName || undefined,
            project_location: intake.projectLocation || undefined,
            project_type: intake.projectType || undefined,
            project_phase: intake.projectStage || undefined,
            project_stage_display: intake.projectStage || undefined,
            documents_available: intake.documentsAvailable,
            design_priorities: intake.designPriorities,
            design_note: intake.designNote || undefined,
            va_design_prepared_by: intake.vaPreparedBy || undefined,
            va_pressures: intake.vaPressures,
            va_prompt: intake.vaPrompt || undefined,
            unsure_notes: intake.unsureNotes || undefined,
            path_switched_from_va: pathSwitchedFromVA || undefined,
            intake_version: "v2",
          },
          uploads,
          hp,
          started_ms: Date.now() - t0,
          journey,
        },
      });
      if (crmRes.error) throw crmRes.error;

      // Prefer canonical reference from the edge function; fall back to id.
      const data = (crmRes.data ?? {}) as { reference?: string; id?: string };
      const ref =
        (data.reference && String(data.reference)) ||
        (data.id ? `CL-${String(data.id).replace(/-/g, "").slice(0, 8).toUpperCase()}` : null);

      safeRecord(
        "intake_v2_submit_success",
        `${path}:${uploads.length}:${Date.now() - t0}`,
      );
      setSuccessRef(ref);
      setSuccess(path);
      toast.success("Request received");
    } catch (err) {
      safeRecord("intake_v2_submit_failure", `${path ?? "unset"}:${(err as Error)?.name ?? "error"}`);
      toast.error("Submission failed", {
        description: "Please try again or email info@clumen.com.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------------- Success ---------------------------- */
  if (success) {
    const copy =
      success === "value_analysis"
        ? {
            eyebrow: "Package Received",
            headline: "Thanks. Your package is with our team.",
            body: "We will review the drawings and fixture information you shared and confirm the right next step within one business day.",
          }
        : success === "unsure"
          ? {
              eyebrow: "Received",
              headline: "Thanks. We have what we need to point you in the right direction.",
              body: "A senior team member will review your notes and recommend a starting point within one business day.",
            }
          : {
              eyebrow: "Project Received",
              headline: "Thanks. Your project is with our team.",
              body: "A senior codelumen team member will review what you shared and reply personally within one business day.",
            };
    const copyRef = async () => {
      if (!successRef) return;
      try {
        await navigator.clipboard.writeText(successRef);
        setCopiedRef(true);
        setTimeout(() => setCopiedRef(false), 2000);
      } catch {
        setCopiedRef(false);
      }
    };
    return (
      <div
        className="border border-white/15 p-8 md:p-10"
        role="status"
        aria-live="polite"
      >
        <p className={`${eyebrowCls} mb-4`}>{copy.eyebrow}</p>
        <h2 className="font-sans font-light text-white text-xl md:text-2xl tracking-[-0.01em] leading-snug">
          {copy.headline}
        </h2>
        <p className="mt-4 text-white/65 font-sans font-light text-sm leading-relaxed">
          {copy.body}
        </p>

        {successRef && (
          <div className="mt-8 border-t border-white/10 pt-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className={eyebrowCls}>Reference</p>
              <p className="mt-2 font-mono text-white text-sm tracking-wider">
                {successRef}
              </p>
            </div>
            <button
              type="button"
              onClick={copyRef}
              aria-label="Copy reference number"
              className="min-h-[40px] px-4 border border-white/25 text-white/80 hover:border-white/60 hover:text-white text-[11px] tracking-[0.2em] uppercase font-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              {copiedRef ? "Copied" : "Copy reference"}
            </button>
          </div>
        )}

        <div className="mt-8 border-t border-white/10 pt-6">
          <p className={eyebrowCls}>What happens next</p>
          <ol className="mt-4 space-y-3 text-white/70 font-sans font-light text-sm leading-relaxed">
            <li className="flex gap-3">
              <span className="text-white/40 w-6 shrink-0">01</span>
              <span>We review your submission and files.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-white/40 w-6 shrink-0">02</span>
              <span>We identify the right starting point and any missing information.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-white/40 w-6 shrink-0">03</span>
              <span>A senior team member contacts you directly.</span>
            </li>
          </ol>
        </div>
      </div>
    );
  }

  /* ---------------------------- Progress ---------------------------- */
  const Progress = (
    <div ref={stepTopRef} className="flex items-center justify-between gap-4 mb-5">
      <p className={eyebrowCls}>
        Step {step} of {totalSteps} · {stepNames[step - 1]}
      </p>
      <div className="flex gap-1.5" aria-hidden>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={i}
            className={`h-[2px] w-8 transition-colors ${
              i < step ? "bg-white/80" : "bg-white/15"
            }`}
          />
        ))}
      </div>
    </div>
  );

  const ErrorSummary =
    errorSummary.length > 0 ? (
      <div
        ref={errorRef}
        tabIndex={-1}
        role="alert"
        aria-live="polite"
        className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-white/90 font-light"
      >
        <p className="text-destructive-foreground font-normal mb-2">
          Please fix the following:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          {errorSummary.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      </div>
    ) : null;

  /* ---------------------------- Render ---------------------------- */
  return (
    <form onSubmit={submit} className="space-y-4 form-mobile-pad" noValidate>
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        aria-hidden="true"
        className="sr-only"
      />
      {Progress}
      {ErrorSummary}

      {/* STEP 1 — Outcome cards */}
      {step === 1 && (
        <fieldset className="space-y-4">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              What would you like help with?
            </h2>
            <p className="mt-2 text-white/55 font-sans font-light text-xs md:text-sm leading-relaxed">
              A few quick questions. About two minutes. A senior team member replies within
              one business day.
            </p>
          </legend>
          <div className="grid grid-cols-1 gap-3">
            {(
              [
                {
                  key: "design",
                  title: "Create or improve the lighting design",
                  desc: "For new projects, renovations, or projects that need lighting direction from concept through execution.",
                  label: "Architectural Lighting Design",
                },
                {
                  key: "value_analysis",
                  title: "Review an existing lighting design",
                  desc: "For projects with drawings or a fixture schedule that need a design, integration, and controls review.",
                  label: "Project Review",
                },
                {
                  key: "unsure",
                  title: "I am not sure yet",
                  desc: "Tell us where the project stands and we will recommend the right starting point.",
                  label: "Project Evaluation",
                },
              ] as { key: V2Path; title: string; desc: string; label: string }[]
            ).map((opt) => {
              const active = path === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPath(opt.key)}
                  aria-pressed={active}
                  className={`relative text-left p-5 md:p-6 border transition-all duration-300 min-h-[44px] ${
                    active ? "border-white bg-white/[0.06]" : "border-white/25 hover:border-white/60"
                  }`}
                >
                  {active && (
                    <span aria-hidden className="absolute left-0 top-0 bottom-0 w-[2px] bg-white" />
                  )}
                  <p className="font-sans font-light text-white text-base tracking-[-0.005em]">
                    {opt.title}
                  </p>
                  <p className="mt-2 text-white/60 font-sans font-light text-xs leading-relaxed">
                    {opt.desc}
                  </p>
                  <p className="mt-3 text-white/40 font-sans font-light text-[10px] tracking-[0.2em] uppercase">
                    {opt.label}
                  </p>
                </button>
              );
            })}
          </div>
          <p className="pt-2 text-white/50 font-sans font-light text-xs leading-relaxed">
            Looking for a private codelumenLabs presentation?{" "}
            <button
              type="button"
              onClick={onLabsClick}
              className="text-white/85 hover:text-white underline underline-offset-4 transition-colors"
            >
              Request a session.
            </button>
          </p>
          <StepNav onBack={null} onNext={goNext} nextLabel="Continue" nextDisabled={!path} />
        </fieldset>
      )}

      {/* STEP 2 — About the project */}
      {step === 2 && (
        <fieldset className="space-y-5">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              Tell us about the project.
            </h2>
            <p className="mt-2 text-white/55 font-sans font-light text-xs md:text-sm leading-relaxed">
              The basics help us route your request to the right senior team member.
            </p>
          </legend>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <FieldWrap
              label={<>Project type{requiredMark}</>}
              error={errors.projectType}
            >
              <Select value={intake.projectType} onValueChange={(v) => setField("projectType", v)}>
                <SelectTrigger aria-label="Project type" className={triggerCls}>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                  {[...FORM_PROJECT_TYPES, "Not sure yet"].map((t) => (
                    <SelectItem key={t} value={t} className={itemCls}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap
              label={<>Where is the project?{requiredMark}</>}
              error={errors.projectLocation}
            >
              <input
                type="text"
                placeholder="City, state or country"
                value={intake.projectLocation}
                onChange={(e) => setField("projectLocation", e.target.value)}
                maxLength={200}
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap
              label={<>{stageLabel}{requiredMark}</>}
              error={errors.projectStage}
            >
              <Select value={intake.projectStage} onValueChange={(v) => setField("projectStage", v)}>
                <SelectTrigger aria-label={stageLabel} className={triggerCls}>
                  <SelectValue placeholder="Choose one" />
                </SelectTrigger>
                <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                  {stageOptions.map((p) => (
                    <SelectItem key={p} value={p} className={itemCls}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrap>
            <FieldWrap label={<>Project name {optionalMark}</>}>
              <input
                type="text"
                placeholder="Only if you would like to share it"
                value={intake.projectName}
                onChange={(e) => setIntake((p) => ({ ...p, projectName: e.target.value }))}
                maxLength={200}
                className={inputCls}
              />
            </FieldWrap>
          </div>
          <StepNav onBack={goBack} onNext={goNext} nextLabel="Continue" />
        </fieldset>
      )}

      {/* STEP 3 — Path-specific */}
      {step === 3 && path === "design" && (
        <fieldset className="space-y-6">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              What would make this project successful?
            </h2>
            <p className="mt-2 text-white/55 font-sans font-light text-xs md:text-sm leading-relaxed">
              Choose up to three. This helps us understand the priorities before we review the project.
            </p>
          </legend>
          <ChipGroup
            legend="Priorities (optional, up to 3)"
            options={DESIGN_PRIORITIES as readonly string[]}
            selected={intake.designPriorities}
            onToggle={(v) => togglePriority("designPriorities", v)}
          />
          <div>
            <label className="block mb-2">
              <span className={eyebrowCls}>Anything specific we should understand?</span>
              <span className="text-white/35 font-sans font-light text-[10px] ml-2">
                (optional · {intake.designNote.length}/500)
              </span>
            </label>
            <Textarea
              rows={3}
              placeholder="One or two sentences is plenty."
              value={intake.designNote}
              onChange={(e) =>
                setIntake((p) => ({ ...p, designNote: e.target.value.slice(0, 500) }))
              }
              className={`${inputCls} resize-none`}
            />
          </div>
          <ChipGroup
            legend="What do you have available today? (optional)"
            options={DESIGN_DOCS as readonly string[]}
            selected={intake.documentsAvailable}
            onToggle={toggleDoc}
          />
          <UploadBlock
            heading="Share anything helpful (optional)"
            helper="Floor plans, reflected ceiling plans, schedules, specifications, renderings, or reference images. Up to 10 files, 300 MB each. Nothing to upload? You can continue without files."
            source="lighting_design"
            files={files}
            setFiles={setFiles}
            path={path}
          />
          <StepNav
            onBack={goBack}
            onNext={goNext}
            nextLabel={uploadsPending ? `Uploading… ${uploadPct ?? 0}%` : "Continue"}
            nextDisabled={uploadsPending || uploadsFailed}
          />
        </fieldset>
      )}

      {step === 3 && path === "value_analysis" && (
        <fieldset className="space-y-6">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              Tell us about the existing lighting design.
            </h2>
            <p className="mt-2 text-white/55 font-sans font-light text-xs md:text-sm leading-relaxed">
              For a project review, the most useful starting point is the latest lighting plan
              and fixture schedule. Upload what you have. If something is missing, we will tell you
              the cleanest next step.
            </p>
          </legend>

          <ChipGroup
            legend="What do you have in hand?"
            options={VA_DOCS as readonly string[]}
            selected={intake.documentsAvailable}
            onToggle={toggleDoc}
          />

          {vaRecommendsSwitch && !vaOverrideKeepPath && (
            <div className="border border-white/25 bg-white/[0.04] p-5 md:p-6">
              <p className={eyebrowCls}>A better starting point</p>
              <p className="mt-3 font-sans font-light text-white text-base md:text-lg tracking-[-0.005em] leading-snug">
                A design engagement may be the better starting point.
              </p>
              <p className="mt-3 text-white/65 font-sans font-light text-xs md:text-sm leading-relaxed">
                A project review depends on an existing lighting layout and fixture schedule
                prepared by others. You can switch paths now, or stay here and our team will
                review what you have.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setPath("design");
                    setPathSwitchedFromVA(true);
                    safeRecord("intake_v2_va_switch_accepted", "value_analysis");
                  }}
                  className="group btn-primary-light min-h-[44px]"
                >
                  <span className="btn-label">
                    Switch to Lighting Design <BtnArrow />
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVaOverrideKeepPath(true);
                    safeRecord("intake_v2_va_switch_declined", "value_analysis");
                  }}
                  className="text-white/60 hover:text-white text-xs tracking-[0.15em] uppercase underline underline-offset-4"
                >
                  Stay with a Project Review
                </button>
              </div>
            </div>
          )}

          <FieldWrap
            label={<>Who prepared the current lighting design?{requiredMark}</>}
            error={errors.vaPreparedBy}
          >
            <Select
              value={intake.vaPreparedBy}
              onValueChange={(v) => setField("vaPreparedBy", v)}
            >
              <SelectTrigger aria-label="Who prepared the design" className={triggerCls}>
                <SelectValue placeholder="Choose one" />
              </SelectTrigger>
              <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                {VA_PREPARED_BY.map((r) => (
                  <SelectItem key={r} value={r} className={itemCls}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-3 text-white/50 font-sans font-light text-[11px] leading-relaxed italic">
              A project review examines an existing lighting design prepared by others.
            </p>
          </FieldWrap>

          <ChipGroup
            legend="Pressure points (optional, up to 3)"
            options={VA_PRESSURES as readonly string[]}
            selected={intake.vaPressures}
            onToggle={(v) => togglePriority("vaPressures", v)}
          />

          <div>
            <label className="block mb-2">
              <span className={eyebrowCls}>What prompted the review?</span>
              <span className="text-white/35 font-sans font-light text-[10px] ml-2">
                (optional · {intake.vaPrompt.length}/500)
              </span>
            </label>
            <Textarea
              rows={2}
              placeholder="A short sentence is enough."
              value={intake.vaPrompt}
              onChange={(e) =>
                setIntake((p) => ({ ...p, vaPrompt: e.target.value.slice(0, 500) }))
              }
              className={`${inputCls} resize-none`}
            />
          </div>

          <UploadBlock
            heading="Attach the current package (optional)"
            helper="Lighting plans, fixture schedules, specifications, RFIs, addenda, or pricing information. Up to 10 files, 300 MB each."
            source="value_analysis"
            files={files}
            setFiles={setFiles}
            path={path}
          />

          <StepNav
            onBack={goBack}
            onNext={goNext}
            nextLabel={uploadsPending ? `Uploading… ${uploadPct ?? 0}%` : "Continue"}
            nextDisabled={uploadsPending || uploadsFailed}
          />
        </fieldset>
      )}

      {step === 3 && path === "unsure" && (
        <fieldset className="space-y-6">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              Tell us what you are trying to solve.
            </h2>
            <p className="mt-2 text-white/55 font-sans font-light text-xs md:text-sm leading-relaxed">
              You do not need to know the right service. We will recommend the best starting point.
            </p>
          </legend>
          <FieldWrap
            label={
              <>
                What is happening with the project?{requiredMark}
                <span className="text-white/35 font-sans font-light text-[10px] ml-2">
                  ({intake.unsureNotes.length}/800)
                </span>
              </>
            }
            error={errors.unsureNotes}
          >
            <Textarea
              rows={5}
              placeholder="Tell us what is decided, what is missing, and what needs to happen next."
              value={intake.unsureNotes}
              onChange={(e) =>
                setIntake((p) => ({ ...p, unsureNotes: e.target.value.slice(0, 800) }))
              }
              className={`${inputCls} resize-none`}
            />
          </FieldWrap>
          <ChipGroup
            legend="What do you have available today? (optional)"
            options={DESIGN_DOCS as readonly string[]}
            selected={intake.documentsAvailable}
            onToggle={toggleDoc}
          />
          <UploadBlock
            heading="Share anything helpful (optional)"
            helper="Anything at all, plans, notes, photos, or references. Up to 10 files, 300 MB each."
            source="lighting_design"
            files={files}
            setFiles={setFiles}
            path={path}
          />
          <StepNav
            onBack={goBack}
            onNext={goNext}
            nextLabel={uploadsPending ? `Uploading… ${uploadPct ?? 0}%` : "Continue"}
            nextDisabled={uploadsPending || uploadsFailed}
          />
        </fieldset>
      )}

      {/* STEP 4 — About you */}
      {step === 4 && (
        <fieldset className="space-y-5">
          <legend className="mb-1">
            <h2 className="font-sans font-light text-white text-lg md:text-xl tracking-[-0.01em]">
              How should we reach you?
            </h2>
          </legend>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <FieldWrap label={<>Name{requiredMark}</>} error={errors.name}>
              <input
                type="text"
                placeholder="Full name"
                value={intake.name}
                onChange={(e) => setField("name", e.target.value)}
                maxLength={100}
                autoComplete="name"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap label={<>Email{requiredMark}</>} error={errors.email}>
              <input
                type="email"
                placeholder="you@company.com"
                value={intake.email}
                onChange={(e) => setField("email", e.target.value)}
                maxLength={255}
                autoComplete="email"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap label={<>Phone {optionalMark}</>}>
              <input
                type="tel"
                placeholder=""
                value={intake.phone}
                onChange={(e) => setIntake((p) => ({ ...p, phone: e.target.value.slice(0, 50) }))}
                autoComplete="tel"
                className={inputCls}
              />
            </FieldWrap>
            <FieldWrap
              label={
                <>
                  Company or studio
                  {intake.role === PRIVATE_ROLE || !intake.role
                    ? optionalMark
                    : requiredMark}
                </>
              }
              error={errors.company}
            >
              <input
                type="text"
                placeholder=""
                value={intake.company}
                onChange={(e) => setField("company", e.target.value)}
                maxLength={150}
                autoComplete="organization"
                className={inputCls}
              />
            </FieldWrap>
            <div className="md:col-span-2">
              <FieldWrap label={<>Your role{requiredMark}</>} error={errors.role}>
                <Select value={intake.role} onValueChange={(v) => setField("role", v)}>
                  <SelectTrigger aria-label="Your role" className={triggerCls}>
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border border-white/10 z-[60]">
                    {V2_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className={itemCls}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldWrap>
            </div>
          </div>
          <p className="text-white/45 font-sans font-light text-[11px] leading-relaxed">
            By submitting, you agree to our{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-white underline underline-offset-4"
            >
              Privacy Policy
            </a>
            . We only use this information to respond to your project.
          </p>
          <div className="form-action-bar justify-between">
            <button
              type="button"
              onClick={goBack}
              className="text-white/70 hover:text-white text-xs tracking-[0.15em] uppercase min-h-[44px] px-2"
            >
              ← Back
            </button>
            <button
              type="submit"
              disabled={submitting || uploadPct !== null}
              aria-busy={submitting}
              data-loading={submitting || undefined}
              className="group btn-primary-light justify-center min-h-[48px] flex-1 max-w-[70%]"
            >
              <span className="btn-label">
                {uploadPct !== null
                  ? `Uploading… ${uploadPct}%`
                  : submitting
                    ? "Sending…"
                    : (
                      <>
                        Send Project Details <BtnArrow />
                      </>
                    )}
              </span>
              <BtnSpinner label="Sending project details" />
            </button>
          </div>
          <p className="text-white/40 font-sans font-light text-[11px] leading-relaxed text-center">
            A senior team member replies within one business day.
          </p>
        </fieldset>
      )}
    </form>
  );
};

/* ---------------------------- Sub-components ---------------------------- */

const FieldWrap = ({
  label,
  children,
  error,
}: {
  label?: React.ReactNode;
  children: React.ReactNode;
  error?: string;
}) => (
  <div>
    {label && (
      <p className={`${eyebrowCls} mb-2`}>{label}</p>
    )}
    {children}
    {error && (
      <p className="mt-2 text-destructive text-[11px] font-light" role="alert">
        {error}
      </p>
    )}
  </div>
);

const ChipGroup = ({
  legend,
  options,
  selected,
  onToggle,
}: {
  legend: string;
  options: readonly string[];
  selected: string[];
  onToggle: (v: string) => void;
}) => (
  <fieldset>
    <legend className={`${eyebrowCls} mb-3`}>{legend}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((v) => {
        const active = selected.includes(v);
        return (
          <button
            key={v}
            type="button"
            role="checkbox"
            aria-checked={active}
            onClick={() => onToggle(v)}
            className={`${chipBase} ${active ? chipActive : chipInactive}`}
          >
            {v}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const UploadBlock = ({
  heading,
  helper,
  source,
  files,
  setFiles,
  path,
}: {
  heading: string;
  helper: string;
  source: "lighting_design" | "value_analysis";
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  path?: V2Path | null;
}) => (
  <div>
    <p className={eyebrowCls}>{heading}</p>
    <p className="mt-2 mb-3 text-white/55 font-sans font-light text-xs leading-relaxed">
      {helper}
    </p>
    <FileUploader
      source={source}
      files={files}
      onChange={setFiles}
      variant="dark"
      onUploadStart={({ count }) =>
        safeRecord("intake_v2_upload_started", `${path ?? "unset"}:${count}`)
      }
      onUploadComplete={({ durationMs }) =>
        safeRecord("intake_v2_upload_completed", `${path ?? "unset"}:${durationMs}`)
      }
      onUploadFailed={({ errorClass }) =>
        safeRecord("intake_v2_upload_failed", `${path ?? "unset"}:${errorClass}`)
      }
      onUploadRemoved={({ remaining }) =>
        safeRecord("intake_v2_upload_removed", `${path ?? "unset"}:${remaining}`)
      }
    />
    <p className="mt-3 text-white/45 font-sans font-light text-[11px] leading-relaxed">
      A codelumen team member reviews every file directly.
    </p>
  </div>
);

const StepNav = ({
  onBack,
  onNext,
  nextLabel,
  nextDisabled,
}: {
  onBack: (() => void) | null;
  onNext: () => void;
  nextLabel: string;
  nextDisabled?: boolean;
}) => (
  <div className="form-action-bar justify-between">
    {onBack ? (
      <button
        type="button"
        onClick={onBack}
        className="text-white/70 hover:text-white text-xs tracking-[0.15em] uppercase min-h-[44px] px-2"
      >
        ← Back
      </button>
    ) : (
      <span />
    )}
    <button
      type="button"
      onClick={onNext}
      disabled={nextDisabled}
      className="group btn-primary-light justify-center min-h-[48px] flex-1 max-w-[70%] disabled:opacity-50"
    >
      <span className="btn-label">
        {nextLabel} <BtnArrow />
      </span>
    </button>
  </div>
);

/* ---------------------------- Analytics helper ---------------------------- */

/**
 * Type-safe wrapper for intake v2 analytics. Only accepts EventKinds
 * explicitly whitelisted for this form. Payload strings never contain
 * PII, filenames, storage keys, or textarea contents — enforced by the
 * call sites below and asserted in the analytics-privacy tests.
 */
type IntakeV2Event = Extract<EventKind, `intake_v2_${string}`>;
function safeRecord(kind: IntakeV2Event, value: string) {
  try {
    record(kind, value);
  } catch {
    /* analytics is opt-in and non-blocking */
  }
}

export default SteppedConsultationFormV2;
